import { IonContent, IonGrid, IonPage, useIonToast } from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import InputField from '../../../components/InputField';
import PageTitle from '../../../components/PageTitle';
import BrandedHeader from '../../components/BrandedHeader';
import Button from '../../../components/Button';
import GoogleSignInButton from '../../components/GoogleSignIn';
import { lockClosed, mail } from 'ionicons/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeIn, slideUpFromBottom } from '../../../animation';
import {
  FieldError,
  FieldValues,
  useForm,
  UseFormRegister,
} from 'react-hook-form';
import { supabase } from '../../../helpers/supabase/supabaseClient';
import { useAuth } from '../../services/auth.services';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { RootState } from '../../../store/store';
import { LoadingState } from '../../../@types/app-types';

interface FormState {
  email: string;
  password: string;
}

export default function SignUp() {
  const auth = useAuth();
  const { signupLoading } = useSelector((store: RootState) => store.auth);
  const history = useHistory();
  //Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [present, dismiss] = useIonToast();

  useEffect(() => {
    // once signup is "finished", navigate to onboarding
    if (signupLoading === LoadingState.LOADED) {
      history.push('/onboarding');
    }
  }, [signupLoading]);

  const handleSignUp = async (data: FormState) => {
    try {
      await auth.signUp(data.email, data.password);
    } catch {
      present({
        buttons: [{ text: 'Close', handler: () => dismiss() }],
        message: `The following error occurred: ${errors}`,
        duration: 2000,
      });
    }
  };

  const handleSignUpWithGoogle = async () => {
    try {
      await auth.logInWithGoogle();
    } catch (error) {
      present({
        buttons: [{ text: 'Close', handler: () => dismiss() }],
        message: `There was a problem logging in with Google`,
        duration: 2000,
      });
      console.error(error);
    }
  };

  //Animate states
  const contentVariants = {
    hidden: {
      opacity: 0,
      y: 25,
      transition: {
        delay: 0.25,
        staggerChildren: 0.4,
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.3,
        when: 'beforeChildren',
      },
    },
    exit: {
      opacity: 0,
    },
  };

  return (
    <IonPage>
      <IonContent>
        <BrandedHeader />
        <IonGrid fixed>
          <AnimatePresence exitBeforeEnter>
            <motion.section
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={contentVariants}
              className="mt-28 px-8 text-center"
            >
              <motion.div
                variants={fadeIn}
                drag
                dragConstraints={{
                  top: -1,
                  left: -1,
                  right: 1,
                  bottom: 1,
                }}
              >
                <PageTitle title="Sign up" className="mb-8" />
              </motion.div>
              <motion.div variants={fadeIn}>
                <GoogleSignInButton
                  text="Sign up"
                  onClick={() => handleSignUpWithGoogle()}
                />
              </motion.div>
              <motion.p variants={fadeIn} className="my-4 text-sm">
                or sign up with email...
              </motion.p>
              <form>
                <motion.div variants={fadeIn}>
                  <InputField
                    label="Email"
                    name="email"
                    type="email"
                    icon={mail}
                    placeholder="youremail@gmail.com"
                    autoFocus={true}
                    register={register}
                    hasPattern={{
                      value: /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i,
                      message: 'Please use a valid email address',
                    }}
                    error={errors.email as FieldError}
                  />
                </motion.div>
                <motion.div variants={fadeIn}>
                  <InputField
                    label="Password"
                    name="password"
                    type="password"
                    icon={lockClosed}
                    placeholder="password"
                    register={register}
                    isRequired="You must have a password"
                  />
                </motion.div>
              </form>
              <motion.div variants={fadeIn} className="mt-4">
                <Button
                  text="Create profile"
                  onClick={handleSubmit((data) => handleSignUp(data))}
                  className="mt-2"
                />
              </motion.div>
              <motion.div variants={fadeIn}>
                <p className="mt-4">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="no font-semibold text-gray-900 underline"
                  >
                    Log in
                  </Link>
                </p>
              </motion.div>
            </motion.section>
          </AnimatePresence>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
}
